import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  History as HistoryIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
  SmartToy as SmartToyIcon,
  DeleteSweepOutlined as DeleteSweepIcon,
} from '@mui/icons-material';

interface ChatHeaderProps {
  isActivating: boolean;
  setIsOpen: (open: boolean) => void;
  setShowSidebar: (show: boolean) => void;
  onClearChat: () => void;
  hasMessages: boolean;
}

export const ChatHeader: React.FC<ChatHeaderProps> = React.memo(
  ({ isActivating, setIsOpen, setShowSidebar, onClearChat, hasMessages }) => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #F0F0F0',
          backgroundColor: '#FFFFFF',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Bot Avatar */}
          <Box
            sx={{
              position: 'relative',
              width: 40,
              height: 40,
              borderRadius: '12px',
              backgroundColor: '#111111',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              animation: isActivating ? 'geminiPulse 1.8s ease-in-out infinite' : 'none',
            }}
          >
            <SmartToyIcon sx={{ fontSize: 22 }} />
            {/* Green online badge dot */}
            <Box
              sx={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: '#00C853',
                border: '2px solid #FFFFFF',
              }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '14.5px', color: '#1A1A1A' }}>
              ShipMate AI
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00C853' }} />
              <Typography variant="caption" sx={{ color: '#757575', fontSize: '12px' }}>
                Online · Ready to help
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title="Clear Chat messages">
            <span>
              <IconButton
                size="small"
                onClick={onClearChat}
                disabled={!hasMessages}
                aria-label="Clear chat messages"
                sx={{
                  color: '#757575',
                  '&:hover': { backgroundColor: '#F5F5F5' },
                  '&:focus-visible': {
                    outline: '2px solid #111111',
                    outlineOffset: '2px',
                  },
                }}
              >
                <DeleteSweepIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Conversation History">
            <IconButton
              size="small"
              onClick={() => setShowSidebar(true)}
              aria-label="View conversation history"
              sx={{
                color: '#757575',
                '&:hover': { backgroundColor: '#F5F5F5' },
                '&:focus-visible': {
                  outline: '2px solid #111111',
                  outlineOffset: '2px',
                },
              }}
            >
              <HistoryIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => setIsOpen(false)}
            aria-label="Minimize chatbot panel"
            sx={{
              color: '#757575',
              '&:hover': { backgroundColor: '#F5F5F5' },
              '&:focus-visible': {
                outline: '2px solid #111111',
                outlineOffset: '2px',
              },
            }}
          >
            <RemoveIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setIsOpen(false)}
            aria-label="Close chatbot panel"
            sx={{
              color: '#757575',
              '&:hover': { backgroundColor: '#F5F5F5' },
              '&:focus-visible': {
                outline: '2px solid #111111',
                outlineOffset: '2px',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
    );
  },
);

ChatHeader.displayName = 'ChatHeader';
